import { type IconEntry, icons as phosphorCatalog } from "@phosphor-icons/core";
import Fuse from "fuse.js";
import { ENTITY_CONFIG } from "../../config/entities";
import { CUSTOM_ICON_REGISTRY } from "./customIcons";
import type { IconName } from "./Icon";
import { phosphorNames } from "./phosphorNames.generated";

export { isIconName } from "./iconNames";

export interface IconCatalogEntry {
  name: IconName;
  label: string;
  source: "texture" | "phosphor";
}
export interface IconSearchResult extends IconCatalogEntry {
  /** Short search evidence, not the complete metadata or a claim of semantic certainty. */
  matches: string[];
}

const labelForName = (name: string) => name.replace(/([a-z\d])([A-Z])/g, "$1 $2");
const normalize = (value: string) =>
  labelForName(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const compact = (value: string) => normalize(value).replace(/ /g, "");
const customNames = Object.keys(CUSTOM_ICON_REGISTRY) as IconName[];
export const iconCatalog: readonly IconCatalogEntry[] = [
  ...customNames.map((name) => ({ name, label: labelForName(name), source: "texture" as const })),
  ...phosphorNames.map((name) => ({ name, label: labelForName(name), source: "phosphor" as const })),
];

function createSearchIndex() {
  const metadata = new Map<string, IconEntry>(phosphorCatalog.map((icon) => [icon.pascal_name, icon]));
  // Small domain extensions; upstream tags supply the general-purpose vocabulary.
  const domainTerms: Partial<Record<IconName, string[]>> = {
    [ENTITY_CONFIG.meter.icon]: ["AMI", "advanced metering infrastructure", "smart meter"],
  };
  const entities = Object.values(ENTITY_CONFIG);
  const records = iconCatalog.map((entry) => {
    const upstream = metadata.get(entry.name);
    const identities = entities.filter((entity) => entity.icon === entry.name);
    return {
      ...entry,
      aliases: upstream?.alias ? [upstream.alias.name, upstream.alias.pascal_name] : [],
      tags: [...(upstream?.tags.filter((tag) => !tag.startsWith("*")) ?? []), ...(domainTerms[entry.name] ?? [])],
      categories: upstream?.categories ?? [],
      entities: identities.flatMap((entity) => [entity.label.singular, entity.label.plural]),
      descriptions: identities.flatMap((entity) => (entity.description ? [entity.description] : [])),
    };
  });
  const index = new Fuse(records, {
    keys: [
      { name: "label", weight: 4 },
      { name: "aliases", weight: 4 },
      { name: "entities", weight: 4 },
      { name: "tags", weight: 3 },
      { name: "categories", weight: 1 },
      { name: "descriptions", weight: 1 },
    ],
    includeScore: true,
    includeMatches: true,
    ignoreLocation: true,
    threshold: 0.25,
  });
  return { records, index };
}
let searchIndex: ReturnType<typeof createSearchIndex> | undefined;
const stopWords = new Set(
  "a an and are as at be by for from in into is it of on or the this to with icon icons".split(" ")
);

/** Rank metadata locally; only compact candidates, never the index, enter model context. */
export function searchIconCatalog(query: string, limit = 12): IconSearchResult[] {
  const text = normalize(query.slice(0, 600));
  const terms = [...new Set(text.split(" ").filter((term) => term && !stopWords.has(term)))].slice(0, 24);
  if (!terms.length) return [];
  const { records, index } = (searchIndex ??= createSearchIndex());
  const ranked = new Map<IconName, { entry: IconCatalogEntry; score: number; matches: Set<string> }>();
  for (const term of terms) {
    const hits = index.search(term);
    const literal = hits.filter((hit) =>
      hit.matches?.some((match) =>
        normalize(match.value ?? "")
          .split(" ")
          .some((word) => word === term || (term.length >= 4 && word.startsWith(term)))
      )
    );
    // Do not pad a good literal match with unrelated fuzzy guesses (recloser ≠ closet).
    for (const hit of literal.length ? literal : term.length >= 4 ? hits : []) {
      const match = ranked.get(hit.item.name) ?? { entry: hit.item, score: 0, matches: new Set<string>() };
      match.score += 1 + (1 - (hit.score ?? 1));
      for (const evidence of hit.matches ?? []) {
        if (evidence.value && evidence.value.length <= 60) match.matches.add(evidence.value);
      }
      ranked.set(hit.item.name, match);
    }
  }
  // Exact normalized names/aliases and canonical entity labels outrank loose tag matches.
  for (const record of records) {
    const exactName = [record.name, ...record.aliases].some((name) => compact(name) === compact(text));
    const exactEntity = record.entities.some((name) => normalize(name) === text);
    if (exactName || exactEntity) {
      const match = ranked.get(record.name) ?? { entry: record, score: 0, matches: new Set<string>() };
      match.score += exactName ? 1000 : 500;
      match.matches.add(exactName ? record.label : text);
      ranked.set(record.name, match);
    }
  }
  const count = Number.isFinite(limit) ? Math.max(1, Math.min(Math.floor(limit), 24)) : 12;
  return [...ranked.values()]
    .sort(
      (a, b) =>
        b.score - a.score || a.entry.name.length - b.entry.name.length || a.entry.name.localeCompare(b.entry.name)
    )
    .slice(0, count)
    .map(({ entry, matches }) => ({
      name: entry.name,
      label: entry.label,
      source: entry.source,
      matches: [...matches].slice(0, 3),
    }));
}
